import React, { useEffect, useState } from 'react';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Building,
  CreditCard,
  DollarSign,
  ShoppingCart,
  Truck,
  Users,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AnalyticsAPI, DashboardAnalytics } from '../lib/api';
import { formatCurrency } from '../lib/formatters';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import NeedsAttention from '../components/dashboard/NeedsAttention';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsAPI.getDashboardAnalytics({ period: 'month' });
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard analytics:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const fetchOrderDetails = async (orderId: string) => {
    try {
      setLoadingOrderDetails(true);
      const response = await AnalyticsAPI.getOrderDetails(orderId);
      if (response.success) {
        setOrderDetails(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching order details:', err);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleOrderClick = (orderId: string) => {
    setSelectedOrder(orderId);
    fetchOrderDetails(orderId);
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setOrderDetails(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load dashboard'}</p>
          <Button onClick={fetchDashboardData}>Retry</Button>
        </div>
      </div>
    );
  }

  const { overview, trends } = analytics;

  return (
    <div className="space-y-4 sm:space-y-6 px-1 sm:px-0">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground text-sm sm:text-base">Welcome back, Admin User</p>
      </div>

      {/* Operational alerts — orders that need attention */}
      <NeedsAttention onOpenOrder={handleOrderClick} />

      {/* Key metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{formatCurrency(overview.revenue.totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">Total Orders</p>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{overview.orders.totalOrders}</p>
                <p className="text-sm text-muted-foreground">
                  Avg: {formatCurrency(overview.orders.avgOrderValue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">Active Suppliers</p>
              <Building className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{overview.suppliers.activeSuppliers}</p>
                <p className="text-sm text-muted-foreground">
                  {overview.suppliers.verifiedSuppliers} verified
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">Active Users</p>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{overview.users.totalUsers}</p>
                <p className="text-sm text-muted-foreground">
                  {overview.users.verifiedUsers} verified
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and recent orders */}
      <div className="grid gap-4 lg:grid-cols-7">
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle>Revenue Overview</CardTitle>
            <CardDescription>Daily revenue trends</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {overview.revenue.trends.length > 0 ? (
                <div className="flex flex-col h-full">
                  <div className="flex justify-end mb-2">
                    <span className="text-sm text-muted-foreground">Last 7 days</span>
                  </div>
                  <div className="flex-1 flex items-end gap-2">
                    {overview.revenue.trends.slice(-7).map((trend) => {
                      const maxRevenue = Math.max(...overview.revenue.trends.slice(-7).map(t => t.revenue));
                      const height = maxRevenue > 0 ? (trend.revenue / maxRevenue) * 100 : 0;
                      const date = new Date(trend.date);
                      const label = `${date.getMonth() + 1}/${date.getDate()}`;

                      return (
                        <div key={trend.date} className="relative flex-1 flex flex-col items-center group">
                          <div className="w-full flex flex-col items-center" style={{ height: '200px' }}>
                            <div className="flex-1 w-full flex items-end justify-center">
                              <div
                                className="bg-primary w-full rounded-t-md transition-all hover:opacity-80 cursor-pointer"
                                style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
                              ></div>
                            </div>
                          </div>
                          <span className="mt-2 text-xs text-center">{label}</span>
                          <div className="absolute bottom-full mb-2 hidden group-hover:block bg-popover text-popover-foreground px-3 py-2 rounded text-xs whitespace-nowrap shadow-lg border z-10">
                            <div className="font-medium">{date.toLocaleDateString()}</div>
                            <div className="text-primary">{formatCurrency(trend.revenue)}</div>
                            <div className="text-muted-foreground">{trend.orderCount} orders</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No revenue data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
            <CardDescription>Latest order activities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {trends.recentOrders.slice(0, 5).map((order) => (
                <div
                  key={order._id}
                  className="flex items-center justify-between border-b pb-2 hover:bg-muted/50 p-2 rounded cursor-pointer transition-colors"
                  onClick={() => handleOrderClick(order._id)}
                >
                  <div className="flex-1">
                    <p className="font-medium">
                      {order.trader ? `${order.trader.firstName} ${order.trader.lastName}` : 'N/A'}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{order.orderNumber}</span>
                      <span>•</span>
                      <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <div>
                      <p className="font-medium">{formatCurrency(order.totalAmount)}</p>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={() => navigate('/orders')}>
                View All Orders
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">Order Status</p>
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold">{overview.orders.statusDistribution.pending || 0}</p>
              <p className="text-xs text-muted-foreground">Pending orders</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">Products</p>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold">{overview.products.totalProducts}</p>
              <p className="text-xs text-muted-foreground">
                {overview.products.totalCategories} categories
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">Transporters</p>
                <Truck className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold">{overview.transporters.activeTransporters}</p>
              <p className="text-xs text-muted-foreground">
                {overview.transporters.verifiedTransporters} verified
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">User Activity</p>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold">{overview.users.usersWithOrders}</p>
              <p className="text-xs text-muted-foreground">Users with orders</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order Details Modal */}
      <Dialog open={selectedOrder !== null} onOpenChange={closeOrderModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {loadingOrderDetails ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : orderDetails ? (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order Number</p>
                  <p className="font-medium">{orderDetails.order.orderNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(orderDetails.order.status)}`}>
                    {orderDetails.order.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Order Date</p>
                  <p className="font-medium">{new Date(orderDetails.order.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="font-medium text-lg">{formatCurrency(orderDetails.order.totalAmount)}</p>
                </div>
              </div>

              {/* Customer Info */}
              {orderDetails.order.trader && (
                <div>
                  <h3 className="font-semibold mb-2">Customer Information</h3>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="font-medium">{orderDetails.order.trader.firstName} {orderDetails.order.trader.lastName}</p>
                    <p className="text-sm text-muted-foreground">{orderDetails.order.trader.phoneNumber}</p>
                    {orderDetails.order.trader.email && (
                      <p className="text-sm text-muted-foreground">{orderDetails.order.trader.email}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Supplier Info */}
              {orderDetails.order.assignedSupplier && (
                <div>
                  <h3 className="font-semibold mb-2">Supplier Information</h3>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="font-medium">{orderDetails.order.assignedSupplier.businessName}</p>
                    <p className="text-sm text-muted-foreground">{orderDetails.order.assignedSupplier.ownerName}</p>
                    <p className="text-sm text-muted-foreground">{orderDetails.order.assignedSupplier.mobile}</p>
                    <p className="text-sm text-muted-foreground">{orderDetails.order.assignedSupplier.email}</p>
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div>
                <h3 className="font-semibold mb-2">Order Items</h3>
                <div className="space-y-2">
                  {orderDetails.order.items.map((item: any, index: number) => (
                    <div key={index} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-medium">{item.product.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.quantity} {item.unit} × {formatCurrency(item.pricePerUnit)}
                        </p>
                      </div>
                      <p className="font-medium">{formatCurrency(item.totalPrice)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(orderDetails.order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span>{formatCurrency(orderDetails.order.taxAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Delivery Charge</span>
                    <span>{formatCurrency(orderDetails.order.deliveryCharge)}</span>
                  </div>
                  {orderDetails.order.discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount</span>
                      <span>-{formatCurrency(orderDetails.order.discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total</span>
                    <span>{formatCurrency(orderDetails.order.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
